/** @jsxImportSource @emotion/react */
import React, { useState, useImperativeHandle, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import CustomGrid from 'components/grids/CustomGrid';
import * as wjGridXlsx from '@grapecity/wijmo.grid.xlsx';
import { getExcelFileName } from 'utils/ExcelUtil';
import UtMatrixHookupTagPopup from '../popup/UtMatrixHookupTagPopup';
import { CommonUtil } from 'utils/CommonUtil';
import {
  DefaultNoneNumberCellTemplate,
  DefaultZeroNumberCellTemplate,
} from 'components/grids/NumberCellRenderer';

interface Props {
  eqpResultRowData?: any;
}

/**
 * @desc 결과보기 > [설비별 결과] 탭 내용
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2025.10.03   jshwang12      [고도화] 설비별 결과 탭 추가
 * 2026.08.05   jihwan89       CSR E20260727-000228 Vacuum Pump 추가
 */
const EquipmentResultContent = ({ eqpResultRowData }: Props, ref) => {
  const [flexRef, setFlexRef] = useState<any>();
  const { t } = useTranslation();
  const [isOpenHookupTagPopup, setOpenHookupTagPopup] = useState<boolean>(false);
  const [hookupTagCondition, setHookupTagCondition] = useState<any>();

  useImperativeHandle(ref, () => ({
    btnDetailExcelExport: () => {
      btnDetailExcelExport();
    },
  }));

  const layoutDefinition = useMemo(() => {
    const prevHeaderNames = [
      '_ELEC',
      '_ELEC_DMD',
      '_CDA',
      '_N2',
      '_N2_AVG',
      '_IW',
      '_IW2',
      '_PCW',
      '_WW',
      '_STEAM',
      '_NG',
      '_EX_RETURN',
      '_EX_VENT',
      // 26.08.05 CSR E20260727-000228 Vacuum Pump 추가
      '_VP',
      '_SA',
    ];

    const parentHeaderNames = [
      String(t('ut.label.전기 수용율 적용 전', '전기 수용율 적용 전')),
      String(t('ut.label.전기 수용율 적용 후', '전기 수용율 적용 후')),
      String(t('ut.label.CDA[l/min]', 'CDA[Nl/min]')),
      String(t('ut.label.N2 Peak [l/min]', 'N2 Peak [Nl/min]')),
      String(t('ut.label.N2 AVG [l/min]', 'N2 AVG [Nl/min]')),
      String(t('ut.label.IW[l/min]', 'IW[ℓ/min]')),
      String(t('ut.label.IW2[l/min]', 'FW[ℓ/min]')),
      String(t('ut.label.PCW[l/min]', 'PCW[ℓ/min]')),
      String(t('ut.label.WW[l/min]', 'WW[ℓ/min]')),
      String(t('ut.label.Steam[Ton/hr]', 'Steam[t/hr]')),
      String(t('ut.label.NG[N㎥/hr]', 'NG[N㎥/hr]')),
      String(t('ut.label.Return air[CMH]', 'Return air[CMH]')),
      String(t('ut.label.Exhaust air[CMH]', 'Exhaust air[CMH]')),
      // 26.08.05 CSR E20260727-000228 Vacuum Pump 추가
      String(t('ut.label.Vacuum Pump[CMH]', 'Vacuum Pump[CMH]')),
      String(t('ut.label.Supply Air[CMH]', 'Supply air[CMH]')),
    ];

    const flexLayout = [] as any[];

    if (eqpResultRowData !== undefined) {
      const childHeader: string[] = [];
      if (eqpResultRowData?.col.length > 0) {
        for (let i = 0; i < prevHeaderNames.length; i++) {
          for (let j = 0; j < eqpResultRowData.col.length; j++) {
            const data = "'" + eqpResultRowData.col[j] + "'" + prevHeaderNames[i];
            childHeader.push(data);
          }
        }
      }
      const result = {};

      for (let i = 0; i < childHeader.length; i += eqpResultRowData.col.length) {
        const key = Math.floor(i / eqpResultRowData.col.length);
        result[key] = childHeader.slice(i, i + eqpResultRowData.col.length);
      }

      let prevHeaderName = '';
      if (parentHeaderNames.length > 0) {
        parentHeaderNames.forEach((item, index) => {
          // Header 추가
          if (prevHeaderName !== item) {
            prevHeaderName = item;
            flexLayout.push({ header: item, columns: [] });
          }
          eqpResultRowData.col.forEach((item, i) => {
            flexLayout[flexLayout.length - 1]['columns'].push({
              header: i + 1,
              align: 'right',
              width: '*',
              minWidth: 100,
              binding: result[index][i],
              cellTemplate: DefaultZeroNumberCellTemplate,
            });
          });
        });
      }
    }

    return [
      {
        binding: 'no',
        header: String(t('com.label.NO', 'NO')),
        width: 40,
        isReadOnly: true,
        align: 'center',
        cellTemplate: (grid) => grid.row._idx + 1,
      },
      {
        binding: 'PRDN_PROC_CD_NM',
        header: String(t('ut.label.공정', '공정')),
        minWidth: 80,
        width: 100,
        align: 'center',
      },
      {
        binding: 'EQCL_ID',
        header: String(t('ut.label.설비군', '설비군')),
        minWidth: 80,
        width: 80,
        align: 'center',
      },
      {
        binding: 'STND_EQP_ID',
        header: String(t('ut.label.표준설비코드', '표준설비코드')),
        minWidth: 80,
        width: 111,
        align: 'center',
      },
      {
        binding: 'DTAL_PROC_CD_NM',
        header: String(t('ut.label.세부공정', '세부공정')),
        minWidth: 80,
        width: 90,
      },
      {
        binding: 'EQP_MAIN_NM',
        header: String(t('ut.label.Main', 'Main')),
        minWidth: 80,
        width: 100,
      },
      {
        binding: 'EQP_MCH_NM',
        header: String(t('ut.label.Machine', 'Machine')),
        minWidth: 80,
        width: 120,
      },
      ...flexLayout,
    ];
  }, [eqpResultRowData]);

  const onInitialized = (grid) => {
    setFlexRef(grid);
    grid.loadedRows.addHandler(() => {
      const items = grid.collectionView ? grid.collectionView.sourceCollection : grid.itemsSource;
      if (!items || !Array.isArray(items)) {
        return;
      }

      items.forEach((item, rowIndex) => {
        grid.columns.forEach((col, colIndex) => {
          const value = grid.getCellData(rowIndex, colIndex, false);
          const content = CommonUtil.safeHtmlContents(value, false);

          grid.setCellData(rowIndex, colIndex, content, true);
        });
      });

      grid.invalidate();
    });
  };

  const btnDetailExcelExport = () => {
    const book = wjGridXlsx.FlexGridXlsxConverter.saveAsync(flexRef, {
      includeColumnHeaders: true,
      includeRowHeaders: true,
    });
    book.sheets[0].name = t('ut.label.설비별 결과', '설비별 결과');
    book.saveAsync(getExcelFileName(t('ut.label.설비별 결과', '설비별 결과')));
  };

  return (
    <>
      <CustomGrid
        layoutDefinition={layoutDefinition}
        rowData={eqpResultRowData?.list || []}
        height={(eqpResultRowData?.list || []).length > 0 ? 400 : 400}
        initialized={onInitialized}
        isReadOnly={true}
        isSelector={false}
        isFilter={false}
        allowPinning={false}
      />
      {isOpenHookupTagPopup && (
        <UtMatrixHookupTagPopup
          open={isOpenHookupTagPopup}
          close={() => setOpenHookupTagPopup(false)}
          condition={hookupTagCondition}
        />
      )}
    </>
  );
};

export default React.forwardRef(EquipmentResultContent);
